import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { INDICATOR_WIDTH_OFFSET } from "@miplatform/constants";
import { ICalendarWorkTemplateBase } from "@miplatform/types";
import { Box } from "@mui/material";
import { getAutoAssignWindowOffsets } from "../autoAssignWindow.utils";

export function AutoAssignWindowIndicators({
  workTemplateBases = [],
  hourHeight,
  indicatorWidth,
}: Readonly<AutoAssignWindowIndicatorsProps>) {
  const lines = useMemo(
    () =>
      workTemplateBases.flatMap((template) =>
        template.autoAssignWindows.flatMap((window, windowIndex) => {
          const { top, bottom } = getAutoAssignWindowOffsets(
            window.start,
            window.end,
            hourHeight
          );

          return [
            {
              key: `${template.id}-${windowIndex}-start`,
              top,
              color: template.color,
            },
            {
              key: `${template.id}-${windowIndex}-end`,
              top: bottom,
              color: template.color,
            },
          ];
        })
      ),
    [workTemplateBases, hourHeight]
  );

  if (lines.length === 0) return null;

  return (
    <Box data-testid="auto-assign-window-indicators" width={indicatorWidth}>
      {lines.map((line) => (
        <AutoAssignWindowLine
          key={line.key}
          top={line.top}
          color={line.color}
        />
      ))}
    </Box>
  );
}

function AutoAssignWindowLine({
  top,
  color,
}: Readonly<{ top: number; color: string }>) {
  const { classes } = useLineStyles({ top, color });

  return <Box data-testid="auto-assign-window-line" className={classes.line} />;
}

const useLineStyles = makeStyles<{
  top: number;
  color: string;
}>()((_, { top, color }) => ({
  line: {
    position: "absolute",
    left: INDICATOR_WIDTH_OFFSET,
    right: 0,
    top,
    height: 0,
    borderTop: `1.5px dashed ${color}`,
    pointerEvents: "none",
    width: "inherit",
  },
}));

export interface AutoAssignWindowIndicatorsProps {
  workTemplateBases?: ICalendarWorkTemplateBase[];
  hourHeight: number;
  indicatorWidth: number;
}
